/**
 * @fileoverview Global Error Boundary — class component (React zorunlu).
 * Tüm beklenmedik render hatalarını yakalar.
 * DEV: hata detayı gösterir | PROD: kullanıcı dostu mesaj + Sentry
 */

import React from 'react'
import { captureError } from '../config/sentry'
import { ENV } from '../config/env'

/**
 * @typedef {{ hasError: boolean, error: Error|null, errorInfo: React.ErrorInfo|null }} State
 */

class ErrorBoundary extends React.Component {
  /** @type {State} */
  state = { hasError: false, error: null, errorInfo: null }

  /**
   * @param {Error} error
   * @returns {State}
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error, errorInfo: null }
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} errorInfo
   */
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })
    captureError(error, { componentStack: errorInfo.componentStack })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (!hasError) return children

    // Custom fallback prop varsa onu kullan
    if (fallback) return fallback

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-[#0D172A] px-6"
      >
        <div className="max-w-lg w-full text-center">
          {/* Gold divider top */}
          <div
            className="w-16 h-[2px] mx-auto mb-8"
            style={{
              background: 'linear-gradient(90deg, #BF953F, #FCF6BA, #AA771C)',
            }}
          />

          <p className="text-xs uppercase tracking-[0.3em] text-[#BF953F] font-semibold mb-4">
            Beklenmedik Hata
          </p>

          <h1
            className="text-4xl font-bold mb-4 text-white"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Bir Sorun Oluştu
          </h1>

          <p className="text-white/60 mb-8 leading-relaxed">
            Üzgünüz, beklenmedik bir hata meydana geldi. Lütfen sayfayı yenileyin
            veya ana sayfaya dönün.
          </p>

          {/* DEV: stack trace */}
          {ENV.IS_DEV && error && (
            <details className="text-left mb-8 bg-white/5 border border-white/10 rounded p-4">
              <summary className="text-red-400 text-sm cursor-pointer mb-2 font-mono">
                {error.name}: {error.message}
              </summary>
              <pre className="text-red-300/70 text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                {errorInfo?.componentStack ?? error.stack}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 text-sm font-semibold text-[#0D172A] rounded-sm transition-transform hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #BF953F 0%, #FCF6BA 40%, #B38728 60%, #AA771C 100%)',
              }}
            >
              Sayfayı Yenile
            </button>
            <a
              href="/"
              className="px-6 py-3 text-sm font-semibold text-white/70 border border-white/20 rounded-sm hover:bg-white/5 transition-colors"
            >
              Ana Sayfaya Dön
            </a>
          </div>

          {/* Gold divider bottom */}
          <div
            className="w-16 h-[2px] mx-auto mt-8"
            style={{
              background: 'linear-gradient(90deg, #BF953F, #FCF6BA, #AA771C)',
            }}
          />
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
